import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ChevronDown,
  List,
  ListOrdered,
  Undo,
  Redo,
  Palette,
  Sigma,
  Table,
  Image,
  Heading1,
  Baseline,
  Hash,
  ChevronRight,
  ChevronLeft,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  SeparatorHorizontal,
} from 'lucide-react';
import { clsx } from 'clsx';

const getSelectionListContext = (editor) => {
  const selection = window.getSelection();
  if (!editor || !selection || selection.rangeCount === 0) {
    return { listType: null, listDepth: 0 };
  }

  const anchorNode = selection.anchorNode;
  const element = anchorNode?.nodeType === 3 ? anchorNode.parentElement : anchorNode;
  if (!element || !editor.contains(element)) {
    return { listType: null, listDepth: 0 };
  }

  const listItem = element.closest?.('li');
  const list = listItem?.closest?.('ol, ul');
  if (!list || !editor.contains(list)) {
    return { listType: null, listDepth: 0 };
  }

  let listDepth = 0;
  let ancestor = list;
  while (ancestor && editor.contains(ancestor)) {
    if (ancestor.matches?.('ol, ul')) listDepth += 1;
    ancestor = ancestor.parentElement?.closest?.('ol, ul');
  }

  return {
    listType: list.tagName === 'OL' ? 'ordered' : 'unordered',
    listDepth,
  };
};

// Keep this component at module scope. Defining it inside DocumentToolbar gives
// it a new identity on every toolbar state/position update, which unmounts
// portalled menu controls while the user is interacting with them.
const ToolbarGroup = ({ children }) => (
  <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-1 py-1 shadow-sm">
    {children}
  </div>
);

const ToolbarPill = ({ icon: Icon, label, onClick, buttonRef, onPreserveSelection }) => (
  <button
    type="button"
    ref={buttonRef}
    data-document-toolbar-trigger={buttonRef ? 'true' : undefined}
    onClick={onClick}
    onMouseDown={(event) => {
      onPreserveSelection?.();
      event.preventDefault();
    }}
    className="group inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
  >
    <Icon className="w-4 h-4 text-sigma-700" />
    <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[140px] group-hover:opacity-100">
      {label}
    </span>
  </button>
);

const DocumentToolbar = ({
  editorRef,
  onFormat,
  onInsertMath,
  onInsertImage,
  onInsertHeader,
  onInsertFooter,
  onInsertPageNumber,
  onInsertSectionDivider,
  onTableAction,
  headerFirstPageOnly = true,
  onHeaderFirstPageOnlyChange,
  onPreserveSelection,
  zoom = 100,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  className,
}) => {
  const [activeFormats, setActiveFormats] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [showPageNumberMenu, setShowPageNumberMenu] = useState(false);
  const [pageNumberStart, setPageNumberStart] = useState('1');
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [tableGridHover, setTableGridHover] = useState({ rows: 3, cols: 3 });
  const [, setMenuPositionTick] = useState(0);
  const alignButtonRef = useRef(null);
  const colorButtonRef = useRef(null);
  const pageNumberButtonRef = useRef(null);
  const tableButtonRef = useRef(null);
  const floatingMenuInteractionRef = useRef(false);
  const [fontSize, setFontSize] = useState(14);
  const fontSizeOptions = [10, 12, 14, 16, 18, 24, 32];

  const updateActiveFormats = useCallback(() => {
    // Interacting with a portalled toolbar menu can fire `selectionchange`.
    // Updating toolbar state at that point remounts the nested toolbar groups,
    // replacing the focused input/button before its event can complete.
    if (
      floatingMenuInteractionRef.current ||
      document.activeElement?.closest?.('[data-document-toolbar-floating]')
    ) return;
    const selection = window.getSelection();
    const editor = editorRef?.current;
    let nextFontSize = null;
    let inTable = false;
    let listContext = { listType: null, listDepth: 0 };

    if (selection && selection.rangeCount > 0 && editor) {
      const anchorNode = selection.anchorNode;
      const target = anchorNode?.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode;
      if (target && editor.contains(target)) {
        inTable = !!target.closest?.('td, th');
        listContext = getSelectionListContext(editor);
        const computed = window.getComputedStyle(target);
        const parsed = Number.parseFloat(computed.fontSize);
        if (Number.isFinite(parsed)) nextFontSize = Math.round(parsed);
      }
    }

    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
      justifyFull: document.queryCommandState('justifyFull'),
      // queryCommandState is inconsistent for nested lists in Chromium. The
      // nearest ancestor list is the authoritative state at the caret.
      insertUnorderedList: listContext.listType
        ? listContext.listType === 'unordered'
        : document.queryCommandState('insertUnorderedList'),
      insertOrderedList: listContext.listType
        ? listContext.listType === 'ordered'
        : document.queryCommandState('insertOrderedList'),
      listDepth: listContext.listDepth,
      inTable,
    });
    if (nextFontSize) {
      setFontSize((prev) => (prev === nextFontSize ? prev : nextFontSize));
    }
  }, [editorRef]);

  const closeMenus = useCallback(() => {
    setShowAlignMenu(false);
    setShowColorPicker(false);
    setShowPageNumberMenu(false);
    setShowTableMenu(false);
  }, []);

  useEffect(() => {
    if (!showAlignMenu && !showColorPicker && !showPageNumberMenu && !showTableMenu) return;
    const close = (e) => {
      if (
        e.target.closest('[data-document-toolbar-floating]') ||
        e.target.closest('[data-document-toolbar-trigger]')
      ) {
        return;
      }
      closeMenus();
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [closeMenus, showAlignMenu, showColorPicker, showPageNumberMenu, showTableMenu]);

  useEffect(() => {
    if (!showAlignMenu && !showColorPicker && !showPageNumberMenu && !showTableMenu) return;
    const update = () => setMenuPositionTick((tick) => tick + 1);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showAlignMenu, showColorPicker, showPageNumberMenu, showTableMenu]);

  useEffect(() => {
    document.addEventListener('selectionchange', updateActiveFormats);
    return () => document.removeEventListener('selectionchange', updateActiveFormats);
  }, [updateActiveFormats]);

  const handleFormat = (command, value) => {
    onPreserveSelection?.();
    onFormat(command, value);
    requestAnimationFrame(updateActiveFormats);
  };

  const handleListNesting = (command) => {
    handleFormat(command);
    // Browsers may normalize the nested <ol>/<ul> one frame after execCommand.
    // Refresh again once that structure has settled so the correct list type
    // and nesting level remain active in the toolbar.
    requestAnimationFrame(() => requestAnimationFrame(updateActiveFormats));
  };

  const handleTableAction = (action, options) => {
    onPreserveSelection?.();
    const handled = onTableAction?.(action, options) === true;
    if (handled && (action === 'insertTable' || action === 'deleteTable')) {
      setShowTableMenu(false);
    }
    requestAnimationFrame(updateActiveFormats);
    return handled;
  };

  const ToolbarButton = ({ icon: Icon, label, onClick, active = false, buttonRef }) => (
    <button
      type="button"
      ref={buttonRef}
      data-document-toolbar-trigger={buttonRef ? 'true' : undefined}
      onClick={onClick}
      onMouseDown={(e) => {
        onPreserveSelection?.();
        e.preventDefault();
      }}
      title={label}
      className={clsx(
        'w-9 h-9 flex items-center justify-center rounded-md border border-transparent transition-colors',
        active
          ? 'bg-sigma-700 text-white shadow-sm'
          : 'text-gray-700 hover:bg-gray-100'
      )}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  const floatingMenuStyle = (anchorRef, width) => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return { display: 'none' };
    const viewportPadding = 8;
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
    );
    return {
      position: 'fixed',
      top: rect.bottom + 8,
      left,
      width,
      zIndex: 1000,
    };
  };

  const renderFloatingMenu = (anchorRef, width, children) => {
    if (typeof document === 'undefined') return null;
    return createPortal(
      <div
        data-document-toolbar-floating="true"
        style={floatingMenuStyle(anchorRef, width)}
        onMouseDownCapture={() => {
          floatingMenuInteractionRef.current = true;
        }}
        onMouseUpCapture={() => {
          window.setTimeout(() => {
            floatingMenuInteractionRef.current = false;
          }, 0);
        }}
      >
        {children}
      </div>,
      document.body
    );
  };

  const activeAlign = activeFormats.justifyCenter
    ? { icon: AlignCenter, command: 'justifyCenter', label: 'Align Center' }
    : activeFormats.justifyRight
      ? { icon: AlignRight, command: 'justifyRight', label: 'Align Right' }
      : activeFormats.justifyFull
        ? { icon: AlignJustify, command: 'justifyFull', label: 'Justify' }
        : { icon: AlignLeft, command: 'justifyLeft', label: 'Align Left' };

  return (
    <div className={clsx('overflow-x-auto bg-white border-b border-gray-200 px-3 py-2 sm:px-4', className)}>
      <div className="flex min-w-max items-center gap-3 md:min-w-0 md:flex-wrap">
        <ToolbarGroup>
          <ToolbarButton icon={Undo} label="Undo (Ctrl+Z)" onClick={() => handleFormat('undo')} />
          <ToolbarButton icon={Redo} label="Redo (Ctrl+Y)" onClick={() => handleFormat('redo')} />
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton
            icon={Bold}
            label="Bold (Ctrl+B)"
            active={activeFormats.bold}
            onClick={() => handleFormat('bold')}
          />
          <ToolbarButton
            icon={Italic}
            label="Italic (Ctrl+I)"
            active={activeFormats.italic}
            onClick={() => handleFormat('italic')}
          />
          <ToolbarButton
            icon={Underline}
            label="Underline (Ctrl+U)"
            active={activeFormats.underline}
            onClick={() => handleFormat('underline')}
          />
          <ToolbarButton
            icon={Strikethrough}
            label="Strikethrough"
            active={activeFormats.strikeThrough}
            onClick={() => handleFormat('strikeThrough')}
          />
        </ToolbarGroup>

        <ToolbarGroup>
          <div className="relative">
            <button
              ref={alignButtonRef}
              data-document-toolbar-trigger="true"
              onClick={() => setShowAlignMenu((open) => !open)}
              onMouseDown={(e) => {
                onPreserveSelection?.();
                e.preventDefault();
              }}
              title={activeAlign.label}
              className={clsx(
                'inline-flex items-center gap-1 px-2 h-9 rounded-md border border-transparent transition-colors',
                (activeFormats.justifyLeft || activeFormats.justifyCenter || activeFormats.justifyRight || activeFormats.justifyFull)
                  ? 'bg-sigma-700 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              <activeAlign.icon className="w-4 h-4" />
              <ChevronDown className="w-3 h-3" />
            </button>

            {showAlignMenu && renderFloatingMenu(
              alignButtonRef,
              184,
              <div className="p-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                <div className="flex items-center gap-1">
                  <ToolbarButton
                    icon={AlignLeft}
                    label="Align Left"
                    active={activeFormats.justifyLeft}
                    onClick={() => {
                      handleFormat('justifyLeft');
                      setShowAlignMenu(false);
                    }}
                  />
                  <ToolbarButton
                    icon={AlignCenter}
                    label="Align Center"
                    active={activeFormats.justifyCenter}
                    onClick={() => {
                      handleFormat('justifyCenter');
                      setShowAlignMenu(false);
                    }}
                  />
                  <ToolbarButton
                    icon={AlignRight}
                    label="Align Right"
                    active={activeFormats.justifyRight}
                    onClick={() => {
                      handleFormat('justifyRight');
                      setShowAlignMenu(false);
                    }}
                  />
                  <ToolbarButton
                    icon={AlignJustify}
                    label="Justify"
                    active={activeFormats.justifyFull}
                    onClick={() => {
                      handleFormat('justifyFull');
                      setShowAlignMenu(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton
            icon={List}
            label={activeFormats.listDepth
              ? `Bullet List (level ${activeFormats.listDepth})`
              : 'Bullet List'}
            active={activeFormats.insertUnorderedList}
            onClick={() => handleFormat('insertUnorderedList')}
          />
          <ToolbarButton
            icon={ListOrdered}
            label={activeFormats.listDepth
              ? `Numbered List (level ${activeFormats.listDepth})`
              : 'Numbered List'}
            active={activeFormats.insertOrderedList}
            onClick={() => handleFormat('insertOrderedList')}
          />
          <ToolbarButton
            icon={ChevronRight}
            label="Increase list level (Tab)"
            onClick={() => handleListNesting('indent')}
          />
          <ToolbarButton
            icon={ChevronLeft}
            label="Decrease list level (Shift+Tab)"
            onClick={() => handleListNesting('outdent')}
          />
        </ToolbarGroup>

        <ToolbarGroup>
          <div className="relative">
            <ToolbarButton
              buttonRef={colorButtonRef}
              icon={Palette}
              label="Text Color"
              active={showColorPicker}
              onClick={() => setShowColorPicker(!showColorPicker)}
            />

            {showColorPicker && renderFloatingMenu(
              colorButtonRef,
              190,
              <div className="p-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                <div className="grid grid-cols-6 gap-1">
                  {[
                    '#000000', '#dc2626', '#ea580c', '#d97706',
                    '#65a30d', '#059669', '#0891b2', '#2563eb',
                    '#7c3aed', '#c026d3', '#e11d48', '#64748b',
                  ].map((color) => (
                    <button
                      key={color}
                      className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      onMouseDown={(e) => {
                        onPreserveSelection?.();
                        e.preventDefault();
                      }}
                      onClick={() => {
                        handleFormat('foreColor', color);
                        setShowColorPicker(false);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <select
              className="text-sm border border-gray-200 rounded-md pl-2 pr-8 py-1 bg-white text-gray-700 appearance-none"
              onMouseDown={() => onPreserveSelection?.()}
              onChange={(e) => {
                const parsed = Number.parseInt(e.target.value, 10);
                if (Number.isFinite(parsed)) setFontSize(parsed);
                handleFormat('fontSizePx', e.target.value);
              }}
              value={fontSize}
              title="Text size"
            >
              {!fontSizeOptions.includes(fontSize) && (
                <option value={fontSize}>{fontSize}</option>
              )}
              {fontSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          </div>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarPill icon={Heading1} label="Header" onClick={onInsertHeader} onPreserveSelection={onPreserveSelection} />
          <label
            className="inline-flex h-9 w-9 cursor-pointer items-center justify-center text-gray-600"
            title="Show header on first page only"
            onMouseDown={() => onPreserveSelection?.()}
          >
            <input
              type="checkbox"
              checked={headerFirstPageOnly}
              onChange={(event) => onHeaderFirstPageOnlyChange?.(event.target.checked)}
              aria-label="Show header on first page only"
              className="h-4 w-4 rounded border-gray-300 text-sigma-700 focus:ring-sigma-600"
            />
          </label>
          <ToolbarPill icon={Baseline} label="Footer" onClick={onInsertFooter} onPreserveSelection={onPreserveSelection} />
          <div className="relative">
            <ToolbarPill
              buttonRef={pageNumberButtonRef}
              icon={Hash}
              label="Page #"
              onClick={() => setShowPageNumberMenu((open) => !open)}
              onPreserveSelection={onPreserveSelection}
            />
            {showPageNumberMenu && renderFloatingMenu(
              pageNumberButtonRef,
              224,
              <div
                id="page-number-menu"
                className="bg-white border border-gray-200 rounded-lg shadow-lg py-1"
              >
                <label className="block border-b border-gray-100 px-3 pb-2 pt-1 text-xs font-medium text-gray-600">
                  Start number
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={pageNumberStart}
                    onChange={(event) => setPageNumberStart(event.target.value.replace(/\D/g, ''))}
                    onBlur={() => {
                      const value = Number.parseInt(pageNumberStart, 10);
                      setPageNumberStart(String(Number.isFinite(value) ? Math.max(1, value) : 1));
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 px-2 py-1 text-sm text-gray-800 focus:border-violet-500 focus:ring-violet-500"
                    aria-label="Page numbering start number"
                  />
                </label>
                {[
                  { value: 'footer:left', label: 'Footer Left' },
                  { value: 'footer:center', label: 'Footer Center' },
                  { value: 'footer:right', label: 'Footer Right' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      const value = Number.parseInt(pageNumberStart, 10);
                      onInsertPageNumber?.(opt.value, Number.isFinite(value) ? Math.max(1, value) : 1);
                      setShowPageNumberMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-violet-50 hover:text-violet-800 transition-colors"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton icon={Sigma} label="Insert Math (Ctrl+M)" onClick={onInsertMath} />
          <ToolbarButton
            icon={SeparatorHorizontal}
            label="Insert Section Divider"
            onClick={onInsertSectionDivider}
          />
          <div className="relative">
            <ToolbarButton
              buttonRef={tableButtonRef}
              icon={Table}
              label={activeFormats.inTable ? 'Edit Table' : 'Insert Table'}
              active={showTableMenu || activeFormats.inTable}
              onClick={() => {
                setShowTableMenu((open) => !open);
                setTableGridHover({ rows: 3, cols: 3 });
              }}
            />
            {showTableMenu && renderFloatingMenu(
              tableButtonRef,
              276,
              <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                {activeFormats.inTable ? (
                  <div className="space-y-3">
                    <div>
                      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Rows
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleTableAction('addRowBefore')}
                        >
                          Add above
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleTableAction('addRowAfter')}
                        >
                          Add below
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-red-200 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleTableAction('deleteRow')}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div>
                      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Columns
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleTableAction('addColumnBefore')}
                        >
                          Add left
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleTableAction('addColumnAfter')}
                        >
                          Add right
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-red-200 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleTableAction('deleteColumn')}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="w-full rounded-md border border-red-200 px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleTableAction('deleteTable')}
                    >
                      Delete table
                    </button>
                    <p className="text-[11px] leading-4 text-gray-500">
                      Drag a column border in the table to resize it.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Insert table</span>
                      <span className="text-xs tabular-nums text-gray-500">
                        {tableGridHover.rows} × {tableGridHover.cols}
                      </span>
                    </div>
                    <div
                      className="grid grid-cols-8 gap-1"
                      onMouseLeave={() => setTableGridHover({ rows: 3, cols: 3 })}
                    >
                      {Array.from({ length: 64 }, (_, index) => {
                        const row = Math.floor(index / 8) + 1;
                        const col = (index % 8) + 1;
                        const selected = row <= tableGridHover.rows && col <= tableGridHover.cols;
                        return (
                          <button
                            key={`${row}-${col}`}
                            type="button"
                            aria-label={`Insert ${row} by ${col} table`}
                            title={`${row} × ${col}`}
                            className={clsx(
                              'h-6 w-6 rounded-sm border transition-colors',
                              selected
                                ? 'border-sigma-500 bg-sigma-100'
                                : 'border-gray-200 bg-white hover:border-sigma-300'
                            )}
                            onMouseEnter={() => setTableGridHover({ rows: row, cols: col })}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleTableAction('insertTable', {
                              rows: row,
                              cols: col,
                              withHeaderRow: true,
                            })}
                          />
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[11px] leading-4 text-gray-500">
                      The first row is formatted as a header.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <ToolbarButton icon={Image} label="Insert Image" onClick={onInsertImage} />
        </ToolbarGroup>

        <div className="ml-auto">
          <ToolbarGroup>
            <ToolbarButton icon={ZoomOut} label="Zoom Out" onClick={onZoomOut} />
            <div className="px-3 py-1 rounded-md bg-gray-100 text-sm font-semibold text-gray-700 min-w-[64px] text-center">
              {zoom}%
            </div>
            <ToolbarButton icon={ZoomIn} label="Zoom In" onClick={onZoomIn} />
            <button
              onClick={onResetZoom}
              onMouseDown={(e) => {
                onPreserveSelection?.();
                e.preventDefault();
              }}
              title="Reset Zoom"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </ToolbarGroup>
        </div>

      </div>
    </div>
  );
};

export default DocumentToolbar;
